import {
  QUEST_TEXT_ROLES,
  checkMission,
  lineCount,
  poppinsWidth,
  type QuestIssue,
  type QuestMissionContent,
  resolveGlyphKey,
  tileTypeRole,
  rowTileStep,
  QUEST_TILE_STEPS,
  type AdminBeat,
  type GlyphKey,
  type QuestLineRole,
} from '@kk/domain';
import { Icon, Text, Title2, WasabiButton } from '@kk/ui';
import { createContext, useContext, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';

import { useContentCan, useSuperAdmin } from '@/auth/SessionProvider';
import { LinkButton, Status } from '@/components/bits';
import { CommentsPanel, EditingBanner, useEditingLock, WaitingLine } from '@/screens/WorkingTogether';
import { GlyphPicker } from '@/components/GlyphPicker';
import { deck } from '@/copy/deck';
import {
  useDiscardMissionDraft,
  useErrorMessage,
  useMissionVersions,
  useQuestContent,
  useSetMissionStatus,
  useUpdateMission,
} from '@/data/hooks';
import { relativeTime } from '@/lib/time';
import type { AdminAdventure, AdminMission, AdminQuizItem, AdminQuizTile } from '@/types/wire';

/** A mission holds 2 or 3 beats (CONTENT_ENGINE §5); the API refuses a fourth. */
const MAX_BEATS = 3;

const c = deck.o11;

/** What the editor changes: the fields a child plays, as one working copy saved to the draft in one call. */
interface Work {
  title: string;
  levelName: string;
  mechanic?: AdminMission['mechanic'];
  deepLink: AdminMission['deepLink'];
  beats: AdminBeat[];
  quiz: AdminQuizItem[];
}

function workOf(m: AdminMission): Work {
  return {
    title: m.title,
    levelName: m.levelName ?? '',
    ...(m.mechanic ? { mechanic: m.mechanic } : {}),
    deepLink: m.deepLink ?? null,
    beats: structuredClone((m.beats ?? []) as unknown as AdminBeat[]),
    quiz: structuredClone(m.quiz),
  };
}

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** The working copy as the checker reads it (@kk/domain `QuestMissionContent`). */
function contentOf(w: Work): QuestMissionContent {
  return {
    title: w.title,
    ...(w.levelName ? { levelName: w.levelName } : {}),
    ...(w.mechanic ? { mechanic: w.mechanic } : {}),
    deepLink: w.deepLink ?? null,
    beats: w.beats,
    quiz: w.quiz as QuestMissionContent['quiz'],
  };
}

/** A drafted fix's result, back as the working copy (the fields a fix can touch are the beats and the quiz). */
function workFrom(c: QuestMissionContent, w: Work): Work {
  return { ...w, beats: (c.beats ?? []) as AdminBeat[], quiz: c.quiz as AdminQuizItem[] };
}

/**
 * **The checker, as the author types** (CONTENT_ENGINE §5). One run over the working copy per render; each field shows
 * the issues at its own path, and a drafted fix applies to the working copy — to be saved and reviewed like any words.
 */
const IssuesContext = createContext<{ issues: QuestIssue[]; fix: (i: QuestIssue) => void }>({
  issues: [],
  fix: () => undefined,
});

const samePath = (a: Array<string | number>, b: Array<string | number>) => a.join('.') === b.join('.');

/** The issues at one path, each with its dot, its words, and a drafted fix when there is one. */
function Issues({ path, testID }: { path: Array<string | number>; testID: string }) {
  const { issues, fix } = useContext(IssuesContext);
  const mine = issues.filter((i) => samePath(i.path, path));
  if (mine.length === 0) return null;
  return (
    <div className="issues" data-testid={testID}>
      {mine.map((i) => (
        <div className="issue" key={i.id}>
          <span className={`issue-dot ${i.severity}`} aria-hidden />
          <span className="issue-text">{i.message}</span>
          {i.fix ? (
            <button type="button" className="link" onClick={() => fix(i)} data-testid={`${testID}-fix`}>
              {i.fix.label}
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

/** The fields that differ from what is stored — a save sends only those. */
function patchOf(work: Work, m: AdminMission) {
  const base = workOf(m);
  return {
    ...(work.title !== base.title ? { title: work.title } : {}),
    ...(work.levelName !== base.levelName ? { levelName: work.levelName } : {}),
    ...(work.mechanic !== base.mechanic && work.mechanic ? { mechanic: work.mechanic } : {}),
    ...(work.deepLink !== base.deepLink ? { deepLink: work.deepLink } : {}),
    ...(!same(work.beats, base.beats) ? { beats: work.beats as unknown as Array<Record<string, unknown>> } : {}),
    ...(!same(work.quiz, base.quiz) ? { quiz: work.quiz } : {}),
  };
}

function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}

/**
 * A counter under a drawn string: segments for the limit, and "4 of 6 lines". It measures with the SAME Poppins ruler
 * the publish gate does (@kk/domain `poppinsWidth`), so what the author sees is exactly what the gate will say.
 */
function Gauge({ text, role, testID }: { text: string; role: QuestLineRole; testID: string }) {
  const { lines, limit, over } = lineCount(text, role, poppinsWidth);
  return (
    <div className="gauge" data-testid={testID}>
      <div className="gauge-segs" aria-hidden>
        {Array.from({ length: Math.max(limit, lines) }, (_, k) => (
          <span key={k} className={k < lines ? (k < limit ? 'seg on' : 'seg over') : 'seg'} />
        ))}
      </div>
      <span className={`gauge-text${over ? ' over' : ''}`}>{c.gauge(lines, limit)}</span>
    </div>
  );
}

/** A string a child READS: its role's run and measure, the words, and the counter. */
function Drawn({
  label,
  role,
  value,
  onChange,
  rows = 3,
  path,
  testID,
}: {
  label: string;
  role: QuestLineRole;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
  path: Array<string | number>;
  testID: string;
}) {
  return (
    <div className="field">
      <div className="field-head">
        <span className="field-label">{label}</span>
        <span className="field-spec">{c.spec(role.run, role.measure)}</span>
      </div>
      <textarea
        className="field-text"
        aria-label={label}
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testID}
      />
      <Gauge text={value} role={role} testID={`${testID}-gauge`} />
      <Issues path={path} testID={`${testID}-issues`} />
    </div>
  );
}

const wordCount = (t: string) => t.trim().split(/\s+/).filter(Boolean).length;

function speak(text: string) {
  try {
    speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text.replace(/\*\*/g, ''));
    u.rate = 0.9;
    speechSynthesis.speak(u);
  } catch {
    // no speech in this browser: the words are still on screen
  }
}

/** What a pre-reader HEARS: never drawn, so no line limit — a word count and a way to listen. */
function Heard({
  label,
  value,
  onChange,
  path,
  testID,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  path: Array<string | number>;
  testID: string;
}) {
  return (
    <div className="field">
      <div className="field-head">
        <span className="field-label heard">{label}</span>
        <button type="button" className="link" onClick={() => speak(value)}>
          {c.listen}
        </button>
      </div>
      <textarea
        className="field-text heard"
        aria-label={label}
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testID}
      />
      <span className="gauge-text">{c.heard(wordCount(value))}</span>
      <Issues path={path} testID={`${testID}-issues`} />
    </div>
  );
}

/** Up, down and remove for one item of a list. */
function Arrange({
  index,
  count,
  onMove,
  onRemove,
  testID,
}: {
  index: number;
  count: number;
  onMove: (to: number) => void;
  onRemove?: () => void;
  testID: string;
}) {
  return (
    <div className="arrange">
      {index > 0 ? (
        <button type="button" className="link" onClick={() => onMove(index - 1)} data-testid={`${testID}-up`}>
          {c.moveUp}
        </button>
      ) : null}
      {index < count - 1 ? (
        <button type="button" className="link" onClick={() => onMove(index + 1)} data-testid={`${testID}-down`}>
          {c.moveDown}
        </button>
      ) : null}
      {onRemove ? (
        <button type="button" className="link muted" onClick={onRemove} data-testid={`${testID}-remove`}>
          {c.remove}
        </button>
      ) : null}
    </div>
  );
}

const text = (s?: { text?: string }) => s?.text ?? '';

function BeatCard({
  beat,
  index,
  count,
  onChange,
  onMove,
  onRemove,
}: {
  beat: AdminBeat;
  index: number;
  count: number;
  onChange: (b: AdminBeat) => void;
  onMove: (to: number) => void;
  onRemove: () => void;
}) {
  const choices = beat.choices ?? [];
  const id = `beat-${index}`;
  return (
    <section className="editor-card" data-testid={id}>
      <div className="editor-card-head">
        <div className="editor-card-title">
          <span className="eyebrow">{c.beat(index + 1)}</span>
          <span className="muted">{c.beatKind(choices.length > 0)}</span>
        </div>
        <Arrange index={index} count={count} onMove={onMove} onRemove={onRemove} testID={id} />
      </div>
      <div className="side-by-side">
        <Drawn
          label={c.reader}
          role={QUEST_TEXT_ROLES.beat}
          value={text(beat.prompt)}
          rows={4}
          onChange={(v) => onChange({ ...beat, prompt: { ...beat.prompt, text: v } })}
          path={['beats', index, 'prompt']}
          testID={`${id}-prompt`}
        />
        <Heard
          label={c.prereader}
          value={text(beat.spoken)}
          onChange={(v) => onChange({ ...beat, spoken: { ...beat.spoken, text: v } })}
          path={['beats', index, 'spoken']}
          testID={`${id}-spoken`}
        />
      </div>
      {choices.length > 0 ? (
        <div className="choices">
          <div className="choices-head">
            <span className="field-label">{c.choices}</span>
            <button
              type="button"
              className="link muted"
              onClick={() => {
                // the beat becomes a story on its own: its choice, and the question that led to it, go together
                const rest = { ...beat };
                delete rest.choices;
                delete rest.ask;
                onChange(rest);
              }}
              data-testid={`${id}-remove-choices`}
            >
              {c.removeChoices}
            </button>
          </div>
          {choices.map((ch, j) => (
            <div className="choice-row" key={ch.key}>
              <span className={`choice-disc ${ch.key === 'A' ? 'a' : 'b'}`}>{ch.key}</span>
              <Drawn
                label={c.choice}
                role={QUEST_TEXT_ROLES.choice}
                value={text(ch.label)}
                rows={2}
                onChange={(v) =>
                  onChange({
                    ...beat,
                    choices: choices.map((x, k) => (k === j ? { ...x, label: { ...x.label, text: v } } : x)),
                  })
                }
                path={['beats', index, 'choices', j, 'label']}
                testID={`${id}-choice-${ch.key}`}
              />
              <Drawn
                label={c.consequence}
                role={QUEST_TEXT_ROLES.consequence}
                value={text(ch.consequence)}
                onChange={(v) =>
                  onChange({
                    ...beat,
                    choices: choices.map((x, k) =>
                      k === j ? { ...x, consequence: { ...x.consequence, text: v } } : x,
                    ),
                  })
                }
                path={['beats', index, 'choices', j, 'consequence']}
                testID={`${id}-consequence-${ch.key}`}
              />
            </div>
          ))}
        </div>
      ) : (
        <button
          type="button"
          className="link"
          onClick={() =>
            onChange({
              ...beat,
              choices: [
                { key: 'A', label: { text: '' }, consequence: { text: '' }, preferred: true },
                { key: 'B', label: { text: '' }, consequence: { text: '' } },
              ],
            })
          }
          data-testid={`${id}-add-choices`}
        >
          {c.addChoices}
        </button>
      )}
    </section>
  );
}

/** What a tile draws: its own key, a picture its id happens to name, or type — chosen, or because nothing matches. */
function artOf(tile: AdminQuizTile): { key?: GlyphKey; fromId?: boolean; chosenType?: boolean } {
  if (tile.art === null) return { chosenType: true };
  if (tile.art) return { key: (resolveGlyphKey(tile.art) ?? undefined) as GlyphKey | undefined };
  const byId = resolveGlyphKey(tile.id);
  return byId ? { key: byId as GlyphKey, fromId: true } : {};
}

function QuestionCard({
  item,
  index,
  count,
  onChange,
  onMove,
  onRemove,
}: {
  item: AdminQuizItem;
  index: number;
  count: number;
  onChange: (q: AdminQuizItem) => void;
  onMove: (to: number) => void;
  onRemove: () => void;
}) {
  const [picking, setPicking] = useState<number | null>(null);
  const arts = item.tiles.map(artOf);
  const pictures = arts.filter((a) => a.key).length;
  const allPictures = pictures === item.tiles.length;
  const row = allPictures ? 'pictures' : pictures === 0 ? 'type' : 'mixed';
  const captions = item.tiles.map((t) => t.caption);
  const captionRole = allPictures ? QUEST_TEXT_ROLES.tileCaption : tileTypeRole(captions);
  const id = `question-${index}`;
  const setTile = (ti: number, next: AdminQuizTile) =>
    onChange({ ...item, tiles: item.tiles.map((t, k) => (k === ti ? next : t)) });
  return (
    <section className="editor-card" data-testid={id}>
      <div className="editor-card-head">
        <span className="eyebrow">{c.question(index + 1)}</span>
        <div className="editor-card-title">
          <span className={`row-pill ${row}`} data-testid={`${id}-row`}>
            {c.rows[row]}
          </span>
          <Arrange index={index} count={count} onMove={onMove} onRemove={onRemove} testID={id} />
        </div>
      </div>
      <div className="side-by-side">
        <Drawn
          label={c.readerQuestion}
          role={QUEST_TEXT_ROLES.question}
          value={item.prompt}
          onChange={(v) => onChange({ ...item, prompt: v })}
          path={['quiz', index, 'prompt']}
          testID={`${id}-prompt`}
        />
        <Heard
          label={c.prereader}
          value={text(item.spoken)}
          onChange={(v) => onChange({ ...item, spoken: { ...item.spoken, text: v } })}
          path={['quiz', index, 'spoken']}
          testID={`${id}-spoken`}
        />
      </div>
      <div className="field-head">
        <span className="field-label">{c.tiles}</span>
        <span className="field-spec">
          {allPictures ? c.rowNotePictures : c.rowNoteType(QUEST_TILE_STEPS[rowTileStep(captions)].run)}
        </span>
      </div>
      <div className="tiles">
        {item.tiles.map((tile, ti) => {
          const art = arts[ti]!;
          const isAnswer = item.answerTileId === tile.id;
          return (
            <div className="tile-card" key={`${tile.id}-${ti}`} data-testid={`${id}-tile-${ti}`}>
              <div className="tile-top">
                <div className="tile-well" aria-hidden>
                  {art.key ? <Icon name={art.key} size={28} /> : <span className="tile-type">Aa</span>}
                </div>
                <div className="tile-lines">
                  <span className="tile-art" data-testid={`${id}-tile-${ti}-art`}>
                    {c.artLine(art)}
                  </span>
                  <span className="muted">{c.idLine(tile.id)}</span>
                </div>
              </div>
              <textarea
                className="field-text tile-caption"
                rows={1}
                aria-label={c.idLine(tile.id)}
                value={tile.caption}
                onChange={(e) => setTile(ti, { ...tile, caption: e.target.value })}
                data-testid={`${id}-tile-${ti}-caption`}
              />
              <Gauge text={tile.caption} role={captionRole} testID={`${id}-tile-${ti}-gauge`} />
              <Issues path={['quiz', index, 'tiles', ti, 'caption']} testID={`${id}-tile-${ti}-issues`} />
              <Issues path={['quiz', index, 'tiles', ti, 'art']} testID={`${id}-tile-${ti}-art-issues`} />
              <div className="tile-actions">
                <button
                  type="button"
                  className="tile-button"
                  onClick={() => setPicking(picking === ti ? null : ti)}
                  data-testid={`${id}-tile-${ti}-pick`}
                >
                  {c.picture}
                </button>
                <button
                  type="button"
                  className={`tile-button${isAnswer ? ' answer' : ''}`}
                  aria-pressed={isAnswer}
                  onClick={() => onChange({ ...item, answerTileId: tile.id })}
                  data-testid={`${id}-tile-${ti}-answer`}
                >
                  {isAnswer ? c.answer : c.makeAnswer}
                </button>
                {item.tiles.length > 2 ? (
                  <button
                    type="button"
                    className="link muted"
                    onClick={() => {
                      const tiles = item.tiles.filter((_, k) => k !== ti);
                      onChange({
                        ...item,
                        tiles,
                        answerTileId: isAnswer ? (tiles[0]?.id ?? '') : item.answerTileId,
                      });
                    }}
                    data-testid={`${id}-tile-${ti}-remove`}
                  >
                    {c.remove}
                  </button>
                ) : null}
              </div>
              {picking === ti ? (
                <div className="tile-picker">
                  <div className="field-head">
                    <span className="field-label">{c.pickerTitle(tile.caption)}</span>
                    <button type="button" className="link" onClick={() => setPicking(null)}>
                      {c.close}
                    </button>
                  </div>
                  <GlyphPicker
                    value={tile.art === null ? undefined : (tile.art ?? art.key)}
                    testID={`${id}-tile-${ti}-picker`}
                    onChange={(key) => {
                      // "No picture" is a CHOICE — null, so the tile draws its answer as type and never falls back
                      // to its id (CONTENT_ENGINE §3.3); a picture is written explicitly, even one the id already names
                      setTile(ti, { ...tile, art: key ?? null });
                      setPicking(null);
                    }}
                  />
                </div>
              ) : null}
            </div>
          );
        })}
        {item.tiles.length < 4 ? (
          <button
            type="button"
            className="tile-add"
            onClick={() =>
              onChange({
                ...item,
                tiles: [...item.tiles, { id: `answer-${Date.now().toString(36)}`, caption: c.newTileCaption, art: null }],
              })
            }
            data-testid={`${id}-add-tile`}
          >
            {c.addTile}
          </button>
        ) : null}
      </div>
      <Issues path={['quiz', index, 'tiles']} testID={`${id}-row-issues`} />
      <Issues path={['quiz', index, 'answerTileId']} testID={`${id}-answer-issues`} />
      <Drawn
        label={c.feedback}
        role={QUEST_TEXT_ROLES.feedback}
        value={item.feedback?.correct ?? ''}
        onChange={(v) => onChange({ ...item, feedback: { ...item.feedback, correct: v } })}
        path={['quiz', index, 'feedback', 'correct']}
        testID={`${id}-feedback`}
      />
      {/* the second line older content carries; nothing new is written into it */}
      {item.feedback?.explain ? (
        <Drawn
          label={c.feedbackWrong}
          role={QUEST_TEXT_ROLES.feedback}
          value={item.feedback.explain}
          onChange={(v) => onChange({ ...item, feedback: { ...item.feedback, explain: v } })}
          path={['quiz', index, 'feedback', 'explain']}
          testID={`${id}-feedback-wrong`}
        />
      ) : null}
    </section>
  );
}

function Chips<T extends string | null>({
  options,
  value,
  onPick,
  testID,
}: {
  options: Array<[T, string]>;
  value: T | undefined;
  onPick: (v: T) => void;
  testID: string;
}) {
  return (
    <div className="actions">
      {options.map(([v, label]) => (
        <button
          key={String(v)}
          type="button"
          className={`chip${value === v ? ' on' : ''}`}
          aria-pressed={value === v}
          onClick={() => onPick(v)}
          data-testid={`${testID}-${String(v)}`}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** The publish panel: the two reviews, and Publish only when it would work — with why, when it would not. */
function Gate({
  mission,
  dirty,
  adventure,
  issues,
  onFix,
}: {
  mission: AdminMission;
  dirty: boolean;
  adventure: AdminAdventure;
  issues: QuestIssue[];
  onFix: (i: QuestIssue) => void;
}) {
  const errorMessage = useErrorMessage();
  const update = useUpdateMission();
  const setStatus = useSetMissionStatus();
  const history = useMissionVersions(mission.id);
  // the six roles: a control the caller's role cannot use is not drawn (the API refuses it either way)
  const mayPublish = useContentCan('publish');
  const mayReview = useContentCan('review');
  const [error, setError] = useState<string | null>(null);
  const live = mission.status === 'live';
  const changed = !live || !!mission.hasDraft;
  const run = (fn: () => Promise<unknown>) => {
    setError(null);
    fn().catch((e: unknown) => setError(errorMessage(e)));
  };
  const review = (band: 'reader' | 'prereader', name: string) => {
    const s = mission.scripts[band];
    const reviewed = !!s?.reviewedAt;
    return (
      <div className="review-row" key={band} data-testid={`review-${band}`}>
        <div className="stack-tight">
          <Text variant="labelStrong">{name}</Text>
          <Text variant="bodySm" tone={reviewed ? 'ink' : 'muted'} testID={`review-${band}-state`}>
            {reviewed
              ? s?.reviewedBy
                ? c.reviewedBy(s.reviewedBy, relativeTime(s.reviewedAt!))
                : deck.o06.reviewed
              : changed
                ? c.needsReview
                : c.nothingToReview}
          </Text>
        </div>
        {/* a review is of what is SAVED: there is nothing to pass while the words on screen are not the draft */}
        {changed && !dirty && mayReview ? (
          <button
            type="button"
            className="tile-button"
            onClick={() => run(() => update.mutateAsync({ id: mission.id, scripts: { [band]: { reviewed: !reviewed } } }))}
            data-testid={`review-${band}-toggle`}
          >
            {reviewed ? deck.o06.undoReview : deck.o06.markReviewed}
          </button>
        ) : null}
      </div>
    );
  };
  const reviewedBoth = !!mission.scripts.reader?.reviewedAt && !!mission.scripts.prereader?.reviewedAt;
  const hasContent = (mission.beats?.length ?? 0) > 0 || mission.quiz.length > 0;
  const errors = issues.filter((i) => i.severity === 'error');
  const warnings = issues.filter((i) => i.severity === 'warning');
  // §5: an error blocks publishing and there is no override, so Publish is absent — and the line says why
  const canPublish = !dirty && changed && errors.length === 0 && reviewedBoth && hasContent && !!mission.mechanic;
  const reason = dirty
    ? c.saveFirst
    : !changed
      ? c.nothingToPublish
      : errors.length
        ? c.errorsBlock(errors.length)
        : deck.o06.cannotPublish;
  const listed = (list: QuestIssue[], tone: 'error' | 'warning', testID: string) =>
    list.map((x) => (
      <div className="issue" key={x.id} data-testid={testID}>
        <span className={`issue-dot ${tone}`} aria-hidden />
        <div className="stack-tight">
          <Text variant="labelStrong">{x.where}</Text>
          <Text variant="bodySm">{x.message}</Text>
          {x.fix ? (
            <button type="button" className="link" onClick={() => onFix(x)} data-testid={`${testID}-fix`}>
              {x.fix.label}
            </button>
          ) : null}
        </div>
      </div>
    ));
  const liveVersion = history.data?.versions.find((v) => v.version === history.data?.liveVersion);
  return (
    <aside className="gate" data-testid="gate">
      <span className="eyebrow muted">{c.gate}</span>
      <div className="gate-counts">
        <div className="gate-count" data-testid="gate-errors">
          <span className="gate-number">{errors.length}</span>
          <span className="muted">{c.errorWord(errors.length)}</span>
        </div>
        <div className="gate-count" data-testid="gate-warnings">
          <span className="gate-number">{warnings.length}</span>
          <span className="muted">{c.warningWord(warnings.length)}</span>
        </div>
      </div>
      {listed(errors, 'error', 'gate-error')}
      {review('reader', c.reviewReader)}
      {review('prereader', c.reviewPrereader)}
      {!mayPublish ? null : canPublish ? (
        <WasabiButton
          label={deck.o06.publish}
          onPress={() => run(() => setStatus.mutateAsync({ id: mission.id, status: 'live' }))}
          testID="publish"
        />
      ) : (
        <div className="gate-reason" data-testid="publish-reason">
          {reason}
        </div>
      )}
      {/* the emergency take-down: any one Publisher, at once — no second person (Evan, 2026-09-26) */}
      {mayPublish && live ? (
        <LinkButton
          label={deck.o06.unpublish}
          onPress={() => {
            if (window.confirm(deck.signIn.confirmTakeDown(mission.title)))
              run(() => setStatus.mutateAsync({ id: mission.id, status: 'retired' }));
          }}
          testID="take-down"
        />
      ) : null}
      {liveVersion ? (
        <Text variant="bodySm" tone="muted" testID="live-line">
          {liveVersion.publishedBy === 'the content files'
            ? c.liveLine(liveVersion.version)
            : c.liveLine(liveVersion.version, liveVersion.publishedBy, relativeTime(liveVersion.publishedAt))}
        </Text>
      ) : null}
      {error ? <Status testID="gate-refusal">{error}</Status> : null}
      <Text variant="bodySm" tone="muted">
        {deck.o06.adventureEyebrow(adventure.order, adventure.title)}
      </Text>
      {warnings.length ? (
        <div className="stack" data-testid="gate-warnings-list">
          <span className="eyebrow muted">{c.warningsTitle}</span>
          {listed(warnings, 'warning', 'gate-warning')}
        </div>
      ) : null}
    </aside>
  );
}

function Editor({ mission, adventure, tab }: { mission: AdminMission; adventure: AdminAdventure; tab: 'story' | 'quiz' }) {
  const errorMessage = useErrorMessage();
  const update = useUpdateMission();
  const [work, setWork] = useState<Work>(() => workOf(mission));
  const [error, setError] = useState<string | null>(null);
  const patch = patchOf(work, mission);
  const mayEdit = useContentCan('edit');
  const dirty = Object.keys(patch).length > 0;
  const content = contentOf(work);
  const issues = checkMission(content, { missionId: mission.id });
  const fix = (i: QuestIssue) => {
    if (i.fix) setWork((w) => workFrom(i.fix!.apply(contentOf(w)), w));
  };
  const set = (p: Partial<Work>) => setWork((w) => ({ ...w, ...p }));
  // one editor at a time: the lock is kept while this working copy changes
  useEditingLock(mission.id, dirty ? work : null);
  const save = () => {
    setError(null);
    update.mutateAsync({ id: mission.id, ...patch }).catch((e: unknown) => setError(errorMessage(e)));
  };
  const nextQuestionId = () => {
    const ids = new Set(work.quiz.map((q) => q.id));
    let n = work.quiz.length + 1;
    while (ids.has(`q${n}`)) n++;
    return `q${n}`;
  };

  return (
    <IssuesContext.Provider value={{ issues, fix }}>
    <div className="editor-layout">
      <div className="editor-main">
        <section className="editor-card details" data-testid="details">
          <label className="field">
            <span className="field-label">{c.missionTitle}</span>
            <input
              className="field-input"
              value={work.title}
              onChange={(e) => set({ title: e.target.value })}
              data-testid="mission-title"
            />
          </label>
          <label className="field">
            <span className="field-label">{c.levelName}</span>
            <input
              className="field-input"
              value={work.levelName}
              onChange={(e) => set({ levelName: e.target.value })}
              data-testid="level-name"
            />
          </label>
          <div className="field">
            <span className="field-label">{c.mechanic}</span>
            <Chips
              options={[
                ['story', c.mechanics.story],
                ['picture-quiz', c.mechanics['picture-quiz']],
              ]}
              value={work.mechanic}
              onPick={(mechanic) => set({ mechanic })}
              testID="mechanic"
            />
          </div>
          <div className="field">
            <span className="field-label">{c.pointsAt}</span>
            <Chips
              options={[
                [null, c.deepLinks.none],
                ['chores', c.deepLinks.chores],
                ['savingsGoal', c.deepLinks.savingsGoal],
                ['kreditScore', c.deepLinks.kreditScore],
              ]}
              value={work.deepLink ?? null}
              onPick={(deepLink) => set({ deepLink })}
              testID="deep-link"
            />
          </div>
        </section>

        <Issues path={['beats']} testID="mission-issues" />
        <Issues path={['mission']} testID="mission-length-issues" />
        {tab === 'story' ? (
          <>
            {work.beats.map((beat, i) => (
              <BeatCard
                key={i}
                beat={beat}
                index={i}
                count={work.beats.length}
                onChange={(b) => set({ beats: work.beats.map((x, k) => (k === i ? b : x)) })}
                onMove={(to) => set({ beats: move(work.beats, i, to) })}
                onRemove={() => set({ beats: work.beats.filter((_, k) => k !== i) })}
              />
            ))}
            {work.beats.length < MAX_BEATS ? (
              <button
                type="button"
                className="link"
                onClick={() => set({ beats: [...work.beats, { prompt: { text: '' }, spoken: { text: '' } }] })}
                data-testid="add-beat"
              >
                {c.addBeat}
              </button>
            ) : null}
          </>
        ) : (
          <>
            {work.quiz.map((item, i) => (
              <QuestionCard
                key={item.id}
                item={item}
                index={i}
                count={work.quiz.length}
                onChange={(q) => set({ quiz: work.quiz.map((x, k) => (k === i ? q : x)) })}
                onMove={(to) => set({ quiz: move(work.quiz, i, to) })}
                onRemove={() => set({ quiz: work.quiz.filter((_, k) => k !== i) })}
              />
            ))}
            <button
              type="button"
              className="link"
              onClick={() =>
                set({
                  quiz: [
                    ...work.quiz,
                    {
                      id: nextQuestionId(),
                      prompt: '',
                      spoken: { text: '' },
                      answerTileId: 'answer-1',
                      tiles: [
                        { id: 'answer-1', caption: c.newTileCaption, art: null },
                        { id: 'answer-2', caption: c.newTileCaption, art: null },
                      ],
                    },
                  ],
                })
              }
              data-testid="add-question"
            >
              {c.addQuestion}
            </button>
          </>
        )}

        {error ? <Status testID="save-error">{error}</Status> : null}
        {dirty && mayEdit ? (
          <div className="save-bar">
            <WasabiButton label={c.save} fullWidth={false} onPress={save} testID="save" />
          </div>
        ) : null}
      </div>
      <div className="stack">
        <Gate mission={mission} dirty={dirty} adventure={adventure} issues={issues} onFix={fix} />
        <WaitingLine mission={mission} />
        <CommentsPanel mission={mission} />
      </div>
    </div>
    </IssuesContext.Provider>
  );
}

/**
 * **O11 · O12 · the mission editor** (`Quest Content Engine.dc.html`; CONTENT_ENGINE.md §8; content engine part 2) at
 * `/quest/:adventureId/:missionId`, with `/quiz` for the questions and `/history` for O13. Beats with the reader's words
 * and the pre-reader's side by side, each drawn string with its line counter; choices and consequences; the questions,
 * their tiles and pictures; add, remove and reorder. Every save goes to the DRAFT of a live mission — nothing a child
 * sees changes until it is published — and the old whole-mission script boxes are gone: a child reads the beats.
 */
export function MissionEditorScreen() {
  const superAdmin = useSuperAdmin();
  const errorMessage = useErrorMessage();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { adventureId, missionId } = useParams<{ adventureId: string; missionId: string }>();
  const content = useQuestContent(superAdmin);
  const discard = useDiscardMissionDraft();
  const [flash, setFlash] = useState<string | null>(null);
  const tab: 'story' | 'quiz' = pathname.endsWith('/quiz') ? 'quiz' : 'story';

  if (!superAdmin) return <Status testID="admin-only">{deck.o06.adminOnly}</Status>;
  if (content.error) return <Status>{errorMessage(content.error)}</Status>;
  const adventure = (content.data ?? []).find((a) => a.id === adventureId);
  const mission = adventure?.missions.find((m) => m.id === missionId);
  if (!adventure || !mission) return content.isPending ? null : <Status>{deck.o06.empty}</Status>;
  const base = `/quest/${adventure.id}/${mission.id}`;

  return (
    <div className="stack" data-testid="mission-editor">
      <div className="page-header">
        <div className="page-header-text">
          <div>
            <button type="button" className="link eyebrow" onClick={() => navigate('/quest')} data-testid="to-library">
              {c.library}
            </button>
            <span className="eyebrow muted"> · {c.eyebrow(adventure.order, adventure.title)}</span>
          </div>
          <Title2 tone="ink">{c.heading(mission.order, mission.title)}</Title2>
          <Text variant="bodySm" tone="muted">
            {c.trackLine(adventure.track, mission.levelName, adventure.badge?.name)}
          </Text>
        </div>
        <span className="status-pill" data-testid="mission-status">
          {deck.o06.status(mission.status)}
        </span>
      </div>
      <div className="actions">
        {(['story', 'quiz', 'history'] as const).map((k) => (
          <button
            key={k}
            type="button"
            className={`chip${k === tab ? ' on' : ''}`}
            aria-pressed={k === tab}
            onClick={() => navigate(k === 'story' ? base : `${base}/${k}`)}
            data-testid={`tab-${k}`}
          >
            {c.tabs[k]}
          </button>
        ))}
      </div>
      {mission.hasDraft ? (
        <div className="draft-banner" data-testid="draft-banner">
          <span>{c.draftBanner}</span>
          <button
            type="button"
            className="link"
            onClick={() =>
              discard
                .mutateAsync(mission.id)
                .then(() => setFlash(c.discarded))
                .catch((e: unknown) => setFlash(errorMessage(e)))
            }
            data-testid="discard-draft"
          >
            {c.discardDraft}
          </button>
        </div>
      ) : null}
      <EditingBanner mission={mission} />
      {flash ? <Status testID="editor-flash">{flash}</Status> : null}
      {/* keyed on what is stored, so a save (or a restore elsewhere) starts the working copy again from the server */}
      <Editor key={`${mission.id}:${JSON.stringify(workOf(mission))}`} mission={mission} adventure={adventure} tab={tab} />
    </div>
  );
}
